import React, { useState, useEffect, useCallback } from 'react';
import { AdminAPI } from '../services/portalApi';
import { usePortalAuth } from '../context/PortalAuthContext';
import { PortalConfirmModal } from '../components/PortalConfirmModal';

export const PortalInvite: React.FC = () => {
  const { admin } = usePortalAuth();
  const [email, setEmail] = useState('');
  const [inviteToken, setInviteToken] = useState('');
  const [inviteExpires, setInviteExpires] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [admins, setAdmins] = useState<any[]>([]);
  const [copied, setCopied] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<{ id: string; name: string; action: 'deactivate' | 'reactivate' | 'delete' } | null>(null);

  const isSuperAdmin = admin?.role === 'super_admin';

  const loadAdmins = useCallback(() => {
    AdminAPI.listAdmins().then((res) => setAdmins(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadAdmins();
  }, [loadAdmins]);

  const runConfirm = async () => {
    if (!confirmTarget) return;
    const target = confirmTarget;
    setConfirmTarget(null);
    setError('');
    setSuccess('');
    try {
      if (target.action === 'deactivate') {
        await AdminAPI.deactivateAdmin(target.id);
        setSuccess(`Admin "${target.name}" deactivated`);
      } else if (target.action === 'reactivate') {
        await AdminAPI.reactivateAdmin(target.id);
        setSuccess(`Admin "${target.name}" reactivated`);
      } else {
        await AdminAPI.deleteAdmin(target.id);
        setSuccess(`Admin "${target.name}" permanently deleted`);
      }
      loadAdmins();
    } catch (err: any) {
      setError(err?.response?.data?.detail || `Failed to ${target.action} admin`);
    }
  };

  const handleInvite = async () => {
    setError('');
    setInviteToken('');
    try {
      const { data } = await AdminAPI.invite({ email });
      setInviteToken(data.invite_token);
      setInviteExpires(data.expires_at);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to send invite');
    }
  };

  const inviteUrl = inviteToken
    ? `${window.location.origin}/ip-portal/invite/${inviteToken}`
    : '';

  const copyUrl = () => {
    navigator.clipboard.writeText(inviteUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const activeAdmins = admins.filter((a) => a.is_active);

  return (
    <div>
      <h2 style={{ marginBottom: 24 }}>Invite Admin</h2>

      {error && <div className="portal-alert portal-alert-error">{error}</div>}
      {success && <div className="portal-alert portal-alert-success">{success}</div>}

      {/* Current Admins */}
      <div className="portal-card portal-mb-24">
        <h3>Current Admins ({activeAdmins.length}/2)</h3>
        <div className="portal-table-wrap">
          <table className="portal-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                {isSuperAdmin && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {admins.map((a) => (
                <tr key={a.id}>
                  <td>{a.name}</td>
                  <td>{a.email}</td>
                  <td>
                    <span className="portal-badge portal-badge-blue">{a.role}</span>
                  </td>
                  <td>
                    <span className={`portal-badge ${a.is_active ? 'portal-badge-green' : 'portal-badge-red'}`}>
                      {a.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  {isSuperAdmin && (
                    <td>
                      {a.id === admin?.id ? (
                        <span style={{ opacity: 0.5 }}>—</span>
                      ) : a.is_active ? (
                        <button
                          className="portal-btn portal-btn-danger portal-btn-sm"
                          onClick={() => setConfirmTarget({ id: a.id, name: a.name, action: 'deactivate' })}
                        >
                          Deactivate
                        </button>
                      ) : (
                        <div className="portal-flex portal-gap-8">
                          <button
                            className="portal-btn portal-btn-primary portal-btn-sm"
                            onClick={() => setConfirmTarget({ id: a.id, name: a.name, action: 'reactivate' })}
                            disabled={activeAdmins.length >= 2}
                            title={activeAdmins.length >= 2 ? 'Maximum of 2 active admins reached — deactivate another admin first' : 'Reactivate this admin'}
                          >
                            Reactivate
                          </button>
                          <button
                            className="portal-btn portal-btn-danger portal-btn-sm"
                            onClick={() => setConfirmTarget({ id: a.id, name: a.name, action: 'delete' })}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Invite Form */}
      {activeAdmins.length < 2 ? (
        <div className="portal-card">
          <h3>Send Invite</h3>
          <div className="portal-flex portal-gap-8" style={{ alignItems: 'flex-end' }}>
            <div className="portal-form-group" style={{ flex: 1, marginBottom: 0 }}>
              <label>Email Address</label>
              <input
                className="portal-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="newadmin@example.com"
              />
            </div>
            <button
              className="portal-btn portal-btn-primary"
              onClick={handleInvite}
              disabled={!email}
              style={{ marginBottom: 0 }}
            >
              Generate Invite Link
            </button>
          </div>

          {inviteToken && (
            <div className="portal-alert portal-alert-success portal-mt-16">
              <strong>Invite created!</strong> Share this link with the new admin:
              <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  className="portal-input"
                  readOnly
                  value={inviteUrl}
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                  style={{ flex: 1 }}
                />
                <button className="portal-copy-btn" onClick={copyUrl}>
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <p style={{ fontSize: '0.8rem', marginTop: 8, opacity: 0.8 }}>
                Expires: {new Date(inviteExpires).toLocaleString()}
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="portal-alert portal-alert-info">
          Maximum of 2 admins reached. Deactivate an existing admin before inviting a new one.
        </div>
      )}

      <PortalConfirmModal
        open={!!confirmTarget}
        title={
          confirmTarget?.action === 'delete'
            ? 'Delete Admin'
            : confirmTarget?.action === 'reactivate'
            ? 'Reactivate Admin'
            : 'Deactivate Admin'
        }
        message={
          confirmTarget?.action === 'delete'
            ? `Permanently delete admin "${confirmTarget?.name}"? This frees up their email so they can be re-invited. This cannot be undone.`
            : confirmTarget?.action === 'reactivate'
            ? `Reactivate admin "${confirmTarget?.name}"? They will regain access to the IP portal using their existing passkeys.`
            : `Deactivate admin "${confirmTarget?.name}"? They will lose access to the IP portal and their passkeys will no longer work.`
        }
        confirmLabel={
          confirmTarget?.action === 'delete'
            ? 'Delete'
            : confirmTarget?.action === 'reactivate'
            ? 'Reactivate'
            : 'Deactivate'
        }
        danger={confirmTarget?.action !== 'reactivate'}
        onConfirm={runConfirm}
        onCancel={() => setConfirmTarget(null)}
      />
    </div>
  );
};
